// Promise wrapper around amazon-cognito-identity-js for the Olene sign-in.
//
// Security model (olene-platform/docs/signin.md):
//  - The browser talks to the Olene Cognito pool directly with SRP
//    (USER_SRP_AUTH). The password never goes to the PayClear backend, and SRP
//    never sends it to Cognito in the clear either. USER_PASSWORD_AUTH is never used.
//  - The backend keeps receiving the Cognito ACCESS token as a Bearer token.
//  - Tokens live in the library's own storage (localStorage); signOut() clears it.
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from "amazon-cognito-identity-js";

let pool = null;

export function getPool() {
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: process.env.REACT_APP_PAYCLEAR_USER_POOL_ID,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    });
  }
  return pool;
}

export const newCognitoUser = (username) => new CognitoUser({ Username: username, Pool: getPool() });

// Resolves to one of:
//   { status: "signedIn", session }
//   { status: "newPasswordRequired", user }
//   { status: "totpRequired", user }
// Rejects with the Cognito error (map it with messageFor()).
function authCallbacks(user, resolve, reject) {
  return {
    onSuccess: (session) => resolve({ status: "signedIn", session }),
    onFailure: reject,
    newPasswordRequired: () => resolve({ status: "newPasswordRequired", user }),
    totpRequired: () => resolve({ status: "totpRequired", user }),
    // SMS MFA and forced MFA enrolment aren't part of the Olene flow.
    mfaRequired: () => reject(Object.assign(new Error("Unsupported challenge"), { code: "UnsupportedChallenge" })),
    mfaSetup: () => reject(Object.assign(new Error("Unsupported challenge"), { code: "UnsupportedChallenge" })),
    selectMFAType: () => reject(Object.assign(new Error("Unsupported challenge"), { code: "UnsupportedChallenge" })),
  };
}

export function signIn(username, password) {
  const user = newCognitoUser(username);
  user.setAuthenticationFlowType("USER_SRP_AUTH");
  const details = new AuthenticationDetails({ Username: username, Password: password });
  return new Promise((resolve, reject) => user.authenticateUser(details, authCallbacks(user, resolve, reject)));
}

// Signs in with a one-time ticket from account.olenesoft.com (auth/oleneSso.js): CUSTOM_AUTH, answered
// by the pool's custom-auth triggers. The nonce proves the ticket was asked for by this tab.
export function ticketSignIn(username, ticket, nonce) {
  const user = newCognitoUser(username);
  user.setAuthenticationFlowType("CUSTOM_AUTH");
  return new Promise((resolve, reject) => {
    const done = { onSuccess: (session) => resolve(session), onFailure: reject };
    user.initiateAuth(new AuthenticationDetails({ Username: username }), {
      ...done,
      customChallenge: () => user.sendCustomChallengeAnswer(ticket,
        { ...done, customChallenge: () => reject(new Error("ticket refused")) }, { nonce }),
    });
  });
}

export function completeNewPassword(user, newPassword) {
  return new Promise((resolve, reject) =>
    user.completeNewPasswordChallenge(newPassword, {}, authCallbacks(user, resolve, reject)));
}

export function sendTotp(user, code) {
  return new Promise((resolve, reject) =>
    user.sendMFACode(code, authCallbacks(user, resolve, reject), "SOFTWARE_TOKEN_MFA"));
}

export function forgotPassword(username) {
  const user = newCognitoUser(username);
  return new Promise((resolve, reject) =>
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: reject,
      inputVerificationCode: () => resolve(),
    }));
}

export function confirmNewPassword(username, code, newPassword) {
  const user = newCognitoUser(username);
  return new Promise((resolve, reject) =>
    user.confirmPassword(code, newPassword, { onSuccess: () => resolve(), onFailure: reject }));
}

// PayClear-only: self sign-up (the prod pool allows it; Olene-Dev does not).
// captchaToken: a Cloudflare Turnstile token, sent as validationData "captcha" for the Olene
// pre sign-up guard to verify (not stored on the account).
export function signUp(email, password, name, captchaToken = "") {
  const attrs = [new CognitoUserAttribute({ Name: "email", Value: email })];
  if (name) attrs.push(new CognitoUserAttribute({ Name: "name", Value: name }));
  const validation = captchaToken ? [new CognitoUserAttribute({ Name: "captcha", Value: captchaToken })] : null;
  return new Promise((resolve, reject) =>
    getPool().signUp(email, password, attrs, validation, (err, result) => (err ? reject(err) : resolve(result))));
}

export function confirmSignUp(email, code) {
  const user = newCognitoUser(email);
  return new Promise((resolve, reject) =>
    user.confirmRegistration(code, true, (err) => (err ? reject(err) : resolve())));
}

export function resendSignUpCode(email) {
  const user = newCognitoUser(email);
  return new Promise((resolve, reject) =>
    user.resendConfirmationCode((err) => (err ? reject(err) : resolve())));
}

// ---- session ---------------------------------------------------------------

// Refresh this long before the access token expires.
export const REFRESH_MARGIN_S = 5 * 60;

// The signed-in user's session, refreshed with the refresh token when the
// access token is expired or about to be. Resolves null when nobody is signed in.
export function loadSession() {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err, session) => {
      if (err || !session) return resolve(null);
      const expiresIn = session.getAccessToken().getExpiration() - Math.floor(Date.now() / 1000);
      if (expiresIn > REFRESH_MARGIN_S) return resolve(session);
      user.refreshSession(session.getRefreshToken(), (rErr, fresh) => resolve(rErr ? null : fresh));
    });
  });
}

// cognitoUser.signOut(): revokes the refresh token (the client has token
// revocation enabled) and clears the library's local storage. The local clear
// always happens, even if the revoke call fails or hangs.
export function signOut() {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      user.signOut(); // no callback: local clear only
      resolve();
    };
    setTimeout(finish, 3000);
    try {
      user.signOut(finish);
    } catch {
      finish();
    }
  });
}

// Shape the rest of the app reads (kept compatible with the old
// react-oidc-context user: access_token, id_token, profile, expires_at).
export function toAuthUser(session) {
  if (!session) return null;
  const access = session.getAccessToken();
  const id = session.getIdToken();
  return {
    access_token: access.getJwtToken(),
    id_token: id.getJwtToken(),
    expires_at: access.getExpiration(),
    profile: id.decodePayload(),
  };
}

// Is this browser's Olene session still live? Cognito's GetUser refuses a revoked token (a sign-out
// in any Olene app). false: signed out somewhere; true: fine; null: can't tell (offline).
export function sessionLive() {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((e, s) => {
      if (e || !s) return resolve(e && (e.code || e.name) === "NotAuthorizedException" ? false : null);
      user.getUserData((err) => {
        const code = err && (err.code || err.name);
        resolve(!err ? true : code === "NotAuthorizedException" ? false : null);
      }, { bypassCache: true });
    });
  });
}

// Sign out of every Olene app from here: revokes every session this person has (GlobalSignOut),
// then clears this one. Resolves even if Cognito can't be reached.
export function globalSignOut() {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve();
  return new Promise((resolve) => {
    const local = () => { try { user.signOut(); } catch {} resolve(); };
    const t = setTimeout(local, 3000);
    user.getSession((e) => {
      if (e) { clearTimeout(t); return local(); }
      const done = () => { clearTimeout(t); local(); };
      user.globalSignOut({ onSuccess: done, onFailure: done });
    });
  });
}
